import { useState } from 'react';
import { PlusCircle, Copy, ClipboardPaste } from 'lucide-react';
import { Popover, PopoverTrigger, PopoverContent } from '@/components/ui/popover';
import { TemplateLineItems, type TemplateItem } from './TemplateLineItems';
import { TriStateCheckbox } from '@/components/ui/tri-state-checkbox';
import type { BreakdownTemplate, CommonTemplateItem } from '@jobos/shared';

interface BreakdownAddActionsProps {
  onAddLineItem: () => void;
  onAddProject?: () => void;
  onAddTemplateItem?: (item: TemplateItem) => void;
  usedTemplateIds?: Set<string>;
  commonItems?: CommonTemplateItem[];
  checkedCount: number;
  onCopy: () => void;
  onPaste: () => void;
  isPreviewMode: boolean;
  copyPasteLabels?: boolean;
  selectAllChecked?: boolean;
  selectAllIndeterminate?: boolean;
  selectAllDisabled?: boolean;
  onSelectAll?: () => void;
  /** Show the "From Template..." popover. Defaults to true (job editor behavior). */
  showTemplateItems?: boolean;
  /** Show the Paste button. Defaults to true (job editor behavior). */
  showPaste?: boolean;
  /** Full job type templates offered in the "From Template..." popover. */
  fullTemplates?: BreakdownTemplate[];
  onApplyFullTemplate?: (template: BreakdownTemplate) => void;
  /** Preselects the job's own type in the template picker. */
  jobProjectType?: string | null;
}

export function BreakdownAddActions({
  onAddLineItem,
  onAddProject,
  onAddTemplateItem,
  usedTemplateIds,
  commonItems,
  checkedCount,
  onCopy,
  onPaste,
  isPreviewMode,
  copyPasteLabels = false,
  selectAllChecked = false,
  selectAllIndeterminate = false,
  selectAllDisabled = false,
  onSelectAll,
  showTemplateItems = true,
  showPaste = true,
  fullTemplates,
  onApplyFullTemplate,
  jobProjectType,
}: BreakdownAddActionsProps) {
  const [addOpen, setAddOpen] = useState(false);
  const menuItemClass =
    'flex w-full items-center gap-2 px-2 py-1.5 text-sm text-gray-700 rounded-md hover:bg-gray-100 hover:text-blue-600 transition-colors';

  const copyPasteBtnClass = copyPasteLabels
    ? 'inline-flex items-center gap-1.5 px-3 py-1.5 text-sm font-medium text-gray-700 border border-gray-300 rounded-md hover:border-blue-500 hover:text-blue-600 transition-colors'
    : 'inline-flex items-center justify-center p-1.5 text-gray-700 border border-gray-300 rounded-md hover:border-blue-500 hover:text-blue-600 transition-colors';

  const pasteBtnClass = copyPasteLabels
    ? 'inline-flex items-center gap-1.5 px-3 py-1.5 text-sm font-medium text-gray-700 border border-gray-300 rounded-md hover:border-blue-500 hover:text-blue-600 transition-colors disabled:opacity-50 disabled:cursor-not-allowed disabled:hover:border-gray-300 disabled:hover:text-gray-700'
    : 'inline-flex items-center justify-center p-1.5 text-gray-700 border border-gray-300 rounded-md hover:border-blue-500 hover:text-blue-600 transition-colors disabled:opacity-50 disabled:cursor-not-allowed disabled:hover:border-gray-300 disabled:hover:text-gray-700';

  return (
    <div className="flex items-center gap-2">
      {onSelectAll && (
        <TriStateCheckbox
          checked={selectAllChecked}
          indeterminate={selectAllIndeterminate}
          disabled={selectAllDisabled}
          onChange={onSelectAll}
          title="Select all"
          className="mr-1"
        />
      )}
      {/* Wide layout: inline buttons (xl and up) */}
      <div className="hidden xl:flex items-center gap-2">
        <button
          onClick={onAddLineItem}
          className="inline-flex items-center gap-1.5 px-3 py-1.5 text-sm font-medium text-gray-700 border border-gray-300 rounded-md hover:border-blue-500 hover:text-blue-600 transition-colors"
        >
          <PlusCircle className="h-3.5 w-3.5" />
          Add Line Item
        </button>
        {onAddProject && (
          <button
            onClick={onAddProject}
            className="inline-flex items-center gap-1.5 px-3 py-1.5 text-sm font-medium text-gray-700 border border-gray-300 rounded-md hover:border-green-500 hover:text-green-600 transition-colors"
          >
            <PlusCircle className="h-3.5 w-3.5" />
            Add Project
          </button>
        )}
        {showTemplateItems && (
          <Popover>
            <PopoverTrigger asChild>
              <button className="inline-flex items-center gap-1.5 px-3 py-1.5 text-sm font-medium text-gray-700 border border-gray-300 rounded-md hover:border-blue-500 hover:text-blue-600 transition-colors">
                <PlusCircle className="h-3.5 w-3.5" />
                From Template...
              </button>
            </PopoverTrigger>
            <PopoverContent align="start" className="w-80 p-0 max-h-96 overflow-auto">
              <TemplateLineItems
                onAddItem={onAddTemplateItem ?? (() => {})}
                usedTemplateIds={usedTemplateIds}
                commonItems={commonItems}
                fullTemplates={fullTemplates}
                onApplyFullTemplate={onApplyFullTemplate}
                jobProjectType={jobProjectType}
              />
            </PopoverContent>
          </Popover>
        )}
      </div>

      {/* Narrow layout: collapsed dropdown (below xl) */}
      <div className="xl:hidden">
        <Popover open={addOpen} onOpenChange={setAddOpen}>
          <PopoverTrigger asChild>
            <button
              aria-label="Add"
              className="inline-flex items-center gap-1.5 px-3 py-1.5 text-sm font-medium text-gray-700 border border-gray-300 rounded-md hover:border-blue-500 hover:text-blue-600 transition-colors"
            >
              <PlusCircle className="h-3.5 w-3.5" />
              Add
            </button>
          </PopoverTrigger>
          <PopoverContent align="start" className="w-56 p-1">
            <button
              onClick={() => {
                onAddLineItem();
                setAddOpen(false);
              }}
              className={menuItemClass}
            >
              <PlusCircle className="h-3.5 w-3.5" />
              Add Line Item
            </button>
            {onAddProject && (
              <button
                onClick={() => {
                  onAddProject();
                  setAddOpen(false);
                }}
                className={`${menuItemClass} hover:text-green-600`}
              >
                <PlusCircle className="h-3.5 w-3.5" />
                Add Project
              </button>
            )}
            {showTemplateItems && (
              <Popover>
                <PopoverTrigger asChild>
                  <button className={menuItemClass}>
                    <PlusCircle className="h-3.5 w-3.5" />
                    From Template...
                  </button>
                </PopoverTrigger>
                <PopoverContent
                  align="start"
                  side="right"
                  className="w-80 p-0 max-h-96 overflow-auto"
                >
                  <TemplateLineItems
                    onAddItem={(item) => {
                      onAddTemplateItem?.(item);
                      setAddOpen(false);
                    }}
                    usedTemplateIds={usedTemplateIds}
                    commonItems={commonItems}
                    fullTemplates={fullTemplates}
                    onApplyFullTemplate={
                      onApplyFullTemplate
                        ? (template) => {
                            onApplyFullTemplate(template);
                            setAddOpen(false);
                          }
                        : undefined
                    }
                    jobProjectType={jobProjectType}
                  />
                </PopoverContent>
              </Popover>
            )}
          </PopoverContent>
        </Popover>
      </div>

      {checkedCount > 0 && (
        <button
          onClick={onCopy}
          title={`Copy ${checkedCount} item(s)`}
          className={copyPasteBtnClass}
        >
          <Copy className="h-3.5 w-3.5" />
          {copyPasteLabels && <span>Copy ({checkedCount})</span>}
        </button>
      )}
      {showPaste && (
        <button
          onClick={onPaste}
          disabled={isPreviewMode}
          title="Paste"
          className={pasteBtnClass}
        >
          <ClipboardPaste className="h-3.5 w-3.5" />
          {copyPasteLabels && <span>Paste</span>}
        </button>
      )}
    </div>
  );
}
